import './BrandPositionChart.scss';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ECharts } from 'echarts';
import type ReactECharts from 'echarts-for-react';
import { Skeleton } from 'primereact/skeleton';
import { SELECTED_BRAND_COLOR } from '../../../../../shared/utils/utils';
import { getChartThemeColors, getFirstSeriesPalette } from '../../../../../shared/utils/chartTheme';
import { useTheme } from '../../../../../shared/contexts/theme/ThemeContext';
import LineChart from '../../../../../shared/components/chart/line-chart/LineChart';
import BrandVisibilityBarChart from '../../../../../shared/components/chart/brand-visibility-bar-chart/BrandVisibilityBarChart';
import { PieChartData } from '../../../../../shared/models/common/PieChartData';
import { MonitorEventLogResponse } from '../../../../../shared/models/monitor/MonitorEventLogResponse';
import EmptyPanel from '../../../../../shared/components/empty-panel/EmptyPanel';
import ComputingState, {
  ComputingModel,
} from '../../../../../shared/components/common/computing-state/ComputingState';
import CustomTooltip from '../../../../../shared/components/common/tooltip/CustomTooltip';
import { useTranslation } from 'react-i18next';
import { formatEventsForLineChart } from '../../../../../shared/services/monitor/events/EventNameFormatter';
import { getPanelSettings } from '../../../../../shared/services/user/UserService';
import { AnomalyIncident } from '../../../../../shared/models/anomaly-detection/models/AnomalyIncident';
import {
  AnomalyMetricKey,
  AnomalyMetricSemantics,
} from '../../../../../shared/models/anomaly-detection/models/AnomalyIncidentsResponse';
import { buildBucketLabelFormatters } from '../../../../../shared/helpers/date/bucketDateLabels';
import AnomalyAxisMarkers, {
  groupAnomaliesByChartDate,
} from './components/anomaly-axis-markers/AnomalyAxisMarkers';
import useIsMobile from '../../../../../shared/hooks/global/UseIsMobile';

interface BrandPositionChartProps {
  averagePositionData: (string | number)[][];
  brandScoreData: (string | number)[][];
  monitorHeaders: string[];
  eventsData: MonitorEventLogResponse[] | null;
  hasAnyPendingQuestions: boolean;
  hasProjectStatsData: boolean;
  isLoading: boolean;
  brandScoreAnomalies?: AnomalyIncident[];
  averagePositionAnomalies?: AnomalyIncident[];
  visibilityAnomalies?: AnomalyIncident[];
  metricSemantics?: Record<AnomalyMetricKey, AnomalyMetricSemantics>;
  /** Active bucket period (DAILY/WEEKLY/MONTHLY) — drives X-axis/tooltip date labels. */
  period?: string | null;
  /** When set, the right (brand score %) Y axis still auto-scales but never exceeds this max (e.g. 100). */
  secondAxisMax?: number;
  /**
   * V2 / NEW_STATS: both series are percentages (Brand Score % as first series, Visibility % as
   * second). Renders the "Brand Score & Visibility" title, formats the left axis as %, both axes
   * 0–100, and does NOT invert the first axis (higher = better, unlike average position).
   */
  percentageMode?: boolean;
  /**
   * V2 / percentageMode only: optional average-position series ([date, averagePosition])
   * plotted on the secondary (right) inverted axis. Hidden by default; the user enables it
   * via the in-header toggle. Lower position = better, so the axis is inverted (best at top).
   */
  averagePositionSecondaryData?: (string | number)[][];
  /** Enabled models + readiness → progress bar and rotating "Checking {model}…" in the collecting state. */
  collectingModels?: ComputingModel[];
  // Marki, których statystyki worker jeszcze liczy po włączeniu — ten sam stan „liczymy” co przy nowym projekcie.
  recomputingBrands?: string[];
  /**
   * V2 / percentageMode: enables a line ↔ bar toggle in the header. Bar view shows per-brand visibility
   * as a snapshot — clearer than a sparse line for fresh projects.
   */
  brandVisibilityData?: PieChartData[];
  brandColors?: { name: string; color: string }[] | null;
  /** Days of data shown, for the bar view's "Showing data for N days" footer. */
  daysCount?: number;
  /** Selected brand (dropdown) — always shown in the bar view's top 7. */
  selectedBrand?: string;
  /** Initial chart view (e.g. 'bar' for projects ≤3 days old). User can toggle; not persisted. */
  defaultChartView?: 'line' | 'bar';
  /**
   * Czy seria średniej pozycji ma startować odznaczona (domyślnie tak — panel odsłania ją dopiero
   * kliknięciem w legendę). Raport ustawia `false`, bo slajd jest statyczny: czego nie widać na
   * pierwszy rzut oka, tego czytelnik nie włączy.
   */
  hideAveragePositionInitially?: boolean;
  /** Wystawia instancję ECharts widoku liniowego — raport zrzuca z niej wykres do PDF-a. */
  chartRef?: React.RefObject<ReactECharts>;
  /**
   * Nie czyta ustawień panelu użytkownika (`/api/user/panel-settings`). Udostępniony raport ogląda gość
   * bez sesji — 401 z tego zapytania wylogowywał go na /login.
   */
  skipPanelSettings?: boolean;
}

const BrandPositionChart: React.FC<BrandPositionChartProps> = ({
  averagePositionData,
  brandScoreData,
  monitorHeaders,
  eventsData,
  hasAnyPendingQuestions,
  hasProjectStatsData,
  isLoading,
  collectingModels,
  recomputingBrands = [],
  averagePositionAnomalies = [],
  brandScoreAnomalies = [],
  visibilityAnomalies = [],
  metricSemantics,
  period,
  secondAxisMax,
  percentageMode = false,
  averagePositionSecondaryData,
  brandVisibilityData,
  brandColors,
  daysCount = 0,
  selectedBrand,
  defaultChartView = 'line',
  hideAveragePositionInitially = true,
  chartRef,
  skipPanelSettings = false,
}) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();

  // Line ↔ bar toggle (V2 only). Starts from `defaultChartView` (bar for fresh projects) and syncs to
  // it until the user picks manually; session-only, no persistence.
  const [chartView, setChartView] = useState<'line' | 'bar'>(defaultChartView);
  const [chartViewTouched, setChartViewTouched] = useState(false);
  useEffect(() => {
    if (!chartViewTouched) {
      setChartView(defaultChartView);
    }
  }, [defaultChartView, chartViewTouched]);
  const selectChartView = (view: 'line' | 'bar') => {
    setChartViewTouched(true);
    setChartView(view);
  };
  const canShowBarView = percentageMode && !!brandVisibilityData && brandVisibilityData.length > 0;
  const showBarView = canShowBarView && chartView === 'bar';
  // The bar view shows ALL brands' visibility, so it has data whenever some brand is visible — even if
  // the monitored brand alone has none yet. The line view still depends on the monitored brand's series.
  const hasBarData = !!brandVisibilityData && brandVisibilityData.some((b) => Number(b.value) > 0);
  const hasViewData = showBarView ? hasBarData : hasProjectStatsData;
  // The palettes below read the live `data-theme` (getFirstSeriesPalette / getChartThemeColors),
  // so a theme toggle must recompute them — consume `theme` to re-render and key the memos on it.
  const { theme } = useTheme();
  const [displayMarkers, setDisplayMarkers] = useState<boolean | undefined>(
    skipPanelSettings ? true : undefined
  );
  const [chartInstance, setChartInstance] = useState<ECharts | null>(null);

  // V2 "Brand Score & Visibility": the average-position line is a regular legend series, but it
  // starts DESELECTED (hidden until the user clicks its legend item). It's only offered in
  // percentageMode and only when it actually carries at least one data point.
  const hasAveragePositionData = useMemo(() => {
    if (!percentageMode || !averagePositionSecondaryData) return false;
    return averagePositionSecondaryData.slice(1).some((row) => row[1] !== null && row[1] !== undefined);
  }, [percentageMode, averagePositionSecondaryData]);

  // V2 layout: when the average-position series is present, it goes on the FIRST (left) axis —
  // inverted (lower = better) with raw numbers — reusing the first axis's proven inverted-position
  // rendering (same as the V1 chart). Brand Score % + Visibility % then move to the SECOND (right)
  // axis. The position line stays deselected by default (initiallyHiddenSeries).
  const showPositionOnLeft = percentageMode && hasAveragePositionData;
  const chartEvents = useMemo(() => formatEventsForLineChart(eventsData), [eventsData, i18n.language]);

  const firstChartData =
    showPositionOnLeft && averagePositionSecondaryData
      ? averagePositionSecondaryData
      : averagePositionData;

  // Anomaly markers always key off the stable x-axis date matrix — never the optional
  // average-position series that can be hidden via the legend.
  const anomalyDateMatrix = averagePositionData;

  const inverseY = showPositionOnLeft || !percentageMode;

  const anomalyAnchor = useMemo(() => {
    if (percentageMode && showPositionOnLeft) {
      // Brand Score % + Visibility % stay on yAxisIndex 1 and remain visible when position is hidden.
      return { yAxisIndex: 1, xAxisBottomValue: 0 };
    }

    if (percentageMode) {
      return { yAxisIndex: 0, xAxisBottomValue: 0 };
    }

    let max = 0;
    for (let rowIndex = 1; rowIndex < anomalyDateMatrix.length; rowIndex++) {
      const row = anomalyDateMatrix[rowIndex];
      if (!row) continue;
      for (let colIndex = 1; colIndex < row.length; colIndex++) {
        const value = Number(row[colIndex]);
        if (!Number.isNaN(value) && value > max) max = value;
      }
    }

    return { yAxisIndex: 0, xAxisBottomValue: Math.min(30, Math.ceil(max + 2)) };
  }, [percentageMode, showPositionOnLeft, anomalyDateMatrix]);

  // Locale + period aware date labels: compact range on the axis, full range
  // (with year) in the tooltip. The matrix first column keeps its raw ISO key/range.
  const dateLabelFormatters = useMemo(
    () => buildBucketLabelFormatters(period, i18n.language),
    [period, i18n.language]
  );

  const anomalyMarkers = useMemo(
    () =>
      groupAnomaliesByChartDate(
        anomalyDateMatrix,
        averagePositionAnomalies,
        brandScoreAnomalies,
        visibilityAnomalies
      ),
    [anomalyDateMatrix, averagePositionAnomalies, brandScoreAnomalies, visibilityAnomalies]
  );

  const handleChartReady = useCallback((chart: ECharts) => {
    if (chart.isDisposed()) {
      return;
    }
    setChartInstance(chart);
  }, []);

  useEffect(() => {
    if (isLoading || displayMarkers === undefined) {
      setChartInstance(null);
    }
  }, [isLoading, displayMarkers]);

  // In percentageMode, tighten the Y axis to the data instead of always topping out at 100%:
  // max = highest plotted value + 10, capped at 100. Both series (Brand Score %, Visibility %)
  // live in averagePositionData (row 0 = headers, col 0 = date, cols 1+ = values).
  const percentageMaxValue = useMemo(() => {
    if (!percentageMode) return undefined;
    let max = 0;
    for (let rowIndex = 1; rowIndex < averagePositionData.length; rowIndex++) {
      const row = averagePositionData[rowIndex];
      if (!row) continue;
      for (let colIndex = 1; colIndex < row.length; colIndex++) {
        const value = Number(row[colIndex]);
        if (!Number.isNaN(value) && value > max) max = value;
      }
    }
    return Math.min(100, Math.ceil(max) + 10);
  }, [percentageMode, averagePositionData]);

  const chartHeaders = useMemo(
    () => (showPositionOnLeft ? [t('averagePosition'), ...monitorHeaders] : monitorHeaders),
    [showPositionOnLeft, monitorHeaders, t]
  );

  const initiallyHiddenSeries = useMemo(
    () => (showPositionOnLeft && hideAveragePositionInitially ? [t('averagePosition')] : undefined),
    [showPositionOnLeft, hideAveragePositionInitially, t]
  );

  const firstColorPalette = useMemo(() => {
    if (showPositionOnLeft) {
      return [getChartThemeColors().neutralLine];
    }
    if (percentageMode) {
      return [getFirstSeriesPalette()[3], SELECTED_BRAND_COLOR];
    }
    return [getFirstSeriesPalette()[3]];
  }, [showPositionOnLeft, percentageMode, theme]);

  const secondColorPalette = useMemo(() => {
    if (showPositionOnLeft) {
      return [getFirstSeriesPalette()[3], SELECTED_BRAND_COLOR];
    }
    return [SELECTED_BRAND_COLOR];
  }, [showPositionOnLeft, theme]);

  const yAxisBounds = useMemo(() => {
    if (showPositionOnLeft) {
      return { minValue: 1 };
    }
    if (percentageMode) {
      return { minValue: 0, maxValue: percentageMaxValue };
    }
    return {};
  }, [showPositionOnLeft, percentageMode, percentageMaxValue]);

  const handleLegendClick = useCallback(() => {}, []);

  useEffect(() => {
    if (skipPanelSettings) {
      return;
    }

    let isMounted = true;

    const loadPanelSettings = async (): Promise<void> => {
      try {
        const panelSettings = await getPanelSettings();
        const prefetchedDisplayMarkers =
          panelSettings?.charts?.brandScoreAndAveragePosition?.displayMarkers;

        if (isMounted) {
          const nextDisplayMarkers =
            typeof prefetchedDisplayMarkers === 'boolean' ? prefetchedDisplayMarkers : true;
          setDisplayMarkers((prev) => (prev === nextDisplayMarkers ? prev : nextDisplayMarkers));
        }
      } catch (_error) {
        if (isMounted) {
          setDisplayMarkers(true);
        }
      }
    };

    void loadPanelSettings();

    return () => {
      isMounted = false;
    };
  }, [skipPanelSettings]);

  const isCollecting = !hasViewData && hasAnyPendingQuestions;

  return (
    <>
      <div
        className={`stats__panel brand-position-panel${isCollecting ? ' stats__panel--computing' : ''}`}
      >
        <div className="page-box__title-action">
          <div className="page-box__title page-box__title--flex">
            {showBarView
              ? t('visibility')
              : t(percentageMode ? 'brandScoreAndVisibility' : 'brandScoreAndAveragePosition')}
            <CustomTooltip
              tooltipText={
                showBarView
                  ? 'visibilityBarTooltip'
                  : percentageMode
                  ? 'brandScoreAndVisibilityTooltip'
                  : 'brandScoreAndAveragePositionTooltip'
              }
              addMargin={false}
            />
          </div>
          {canShowBarView && (
            <div className="chart-view-toggle" role="group" aria-label="Chart type">
              <button
                id="chart-view-line"
                type="button"
                className={`chart-view-toggle__btn${
                  chartView === 'line' ? ' chart-view-toggle__btn--active' : ''
                }`}
                onClick={() => selectChartView('line')}
                aria-label={t('lineChart')}
              >
                <span className="ico--activity ico--xs" />
              </button>
              {/* Telefon bez dymków: przy prawej krawędzi wystawały poza ekran, a to poszerzało stronę
                  i przesuwało przyklejony dolny pasek. */}
              {!isMobile && (
                <CustomTooltip tooltipText="brandScoreAndVisibility" targetRef="chart-view-line" />
              )}
              <button
                id="chart-view-bar"
                type="button"
                className={`chart-view-toggle__btn${
                  chartView === 'bar' ? ' chart-view-toggle__btn--active' : ''
                }`}
                onClick={() => selectChartView('bar')}
                aria-label={t('barChart')}
              >
                <span className="ico--chart ico--xs" />
              </button>
              {!isMobile && <CustomTooltip tooltipText="visibility" targetRef="chart-view-bar" />}
            </div>
          )}
        </div>
        {recomputingBrands.length > 0 ? (
          <div className="stats-box stats-box--without-margin stats-box--chart">
            <div className="stats-box__content">
              <ComputingState
                title="statsRecomputingTitle"
                subtitle="statsRecomputingSubtitle"
                subtitleParams={{ brands: recomputingBrands.join(', ') }}
              />
            </div>
          </div>
        ) : !hasViewData && !hasAnyPendingQuestions && !isLoading ? (
          <div className="stats-box stats-box--without-margin stats-box--chart">
            <div className="stats-box__content stats-box__content--empty">
              <EmptyPanel
                textKey="startBuildingAiVisibility"
                descriptionKey="startBuildingAiVisibilityDescription"
              />
            </div>
          </div>
        ) : !hasViewData && hasAnyPendingQuestions ? (
          // Fresh project still collecting → friendly "computing" state: a cross-fading model icon in sync
          // with a rotating "Checking {model}…" line + real progress, instead of a skeleton.
          <div className="stats-box stats-box--without-margin stats-box--chart">
            <div className="stats-box__content">
              <ComputingState title="computingTitle" variant="models" models={collectingModels} />
            </div>
          </div>
        ) : !hasViewData || isLoading || (!showBarView && displayMarkers === undefined) ? (
          <div className="stats-box stats-box--without-margin stats-box--chart">
            <div className="stats-box__content">
              <Skeleton className="skeleton--chart">
                <span />
              </Skeleton>
            </div>
          </div>
        ) : showBarView ? (
          <div className="stats-box stats-box--without-margin stats-box--chart">
            <div className="stats-box__content chart-view-anim" key="bar">
              <BrandVisibilityBarChart
                data={brandVisibilityData ?? []}
                brandColors={brandColors ?? null}
                daysCount={daysCount}
                selectedBrand={selectedBrand}
              />
            </div>
          </div>
        ) : (
          <div className="stats-box stats-box--without-margin stats-box--chart">
            <div className="stats-box__content chart-view-anim" key="line">
              <LineChart
                chartRef={chartRef}
                // V2 (showPositionOnLeft): average position on the LEFT (first) axis — inverted, raw
                // numbers. Otherwise firstData holds the % series (V2 with no position data) or the
                // V1 average-position series.
                firstData={firstChartData}
                firstColorPalette={firstColorPalette}
                // V2 (showPositionOnLeft): Brand Score % + Visibility % move to the RIGHT (second) axis.
                // V1: brand score % on the right. V2 without position data: no second axis.
                secondData={
                  showPositionOnLeft ? averagePositionData : percentageMode ? undefined : brandScoreData
                }
                secondColorPalette={secondColorPalette}
                // Position is listed in the legend but DESELECTED by default (initiallyHiddenSeries).
                headers={chartHeaders}
                initiallyHiddenSeries={initiallyHiddenSeries}
                // Average position lives on the left (firstData) axis but should appear LAST in the
                // legend, after Brand Score & Visibility — so render the secondData group first.
                secondDataLegendFirst={showPositionOnLeft}
                onLegendClick={handleLegendClick}
                // Left-align the legend only in the V2 "Brand Score & Visibility" (percentageMode)
                // chart; the legacy chart keeps the spread-out (SPACE_BETWEEN) layout as before.
                legendType={percentageMode ? 'FLEX_START' : 'SPACE_BETWEEN'}
                // Left axis is inverted for average position (V2 with position, or V1); the % left axis
                // (V2 without position) is not inverted. The right (%) axis is never inverted.
                inverseY={showPositionOnLeft || !percentageMode}
                formatYAsPercentage={percentageMode && !showPositionOnLeft}
                {...yAxisBounds}
                marksData={chartEvents}
                panelSettingsConfig="brandScoreAndAveragePosition"
                prefetchedDisplayMarkers={displayMarkers}
                dateAxisFormatter={dateLabelFormatters.axis}
                dateTooltipFormatter={dateLabelFormatters.tooltip}
                secondAxisMax={showPositionOnLeft ? 100 : percentageMode ? undefined : secondAxisMax}
                onChartReady={anomalyMarkers.length > 0 ? handleChartReady : undefined}
              >
                {anomalyMarkers.length > 0 && metricSemantics && (
                  <AnomalyAxisMarkers
                    chart={chartInstance}
                    markers={anomalyMarkers}
                    metricSemantics={metricSemantics}
                    dateTooltipFormatter={dateLabelFormatters.tooltip}
                    xAxisBottomValue={anomalyAnchor.xAxisBottomValue}
                    yAxisIndex={anomalyAnchor.yAxisIndex}
                  />
                )}
              </LineChart>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BrandPositionChart;
